// src/components/common/TableForm.jsx
// -----------------------------------------------------------------------------
// Reusable, dynamic form generator for both Add and Edit modes.
// Driven by a `fields` configuration array and integrated with `validationSchema`.
// Displays inline errors and manages submit state.
// -----------------------------------------------------------------------------
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { validateField, validateForm } from '../../utils/validationSchema';
import Modal from './Modal';
import './common.css';

export default function TableForm({
  fields = [],
  initialValues = {},
  mode = 'create', // 'create' | 'edit'
  isEdit = mode === 'edit',
  isOpen = true,
  onSubmit,
  onCancel,
  submitting = false,
  serverErrors = {},
  onFieldChange,
  size,
  itemName = 'Record',
}) {
  const title = isEdit ? `Edit ${itemName}` : `Add ${itemName}`;

  // Initialize form state
  const [formData, setFormData] = useState({});
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  const initialValuesString = JSON.stringify(initialValues);

  // Sync initial values when form opens or initialValues change
  useEffect(() => {
    if (!isOpen) return;

    const defaultData = {};
    fields.forEach((f) => {
      if (initialValues && initialValues[f.name] !== undefined && initialValues[f.name] !== null) {
        defaultData[f.name] = initialValues[f.name];
      } else {
        defaultData[f.name] = f.defaultValue !== undefined ? f.defaultValue : '';
      }
    });
    setFormData(defaultData);
    setTouched({});
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesString, isOpen]);

  // Merge client-side validation errors with backend server errors
  const combinedErrors = useMemo(() => {
    return { ...errors, ...serverErrors };
  }, [errors, serverErrors]);

  // Validate form dynamically on value changes
  const checkFormValidity = (currentValues) => {
    const { isValid } = validateForm(fields, currentValues);
    return isValid;
  };

  const isFormValid = useMemo(() => {
    return checkFormValidity(formData);
  }, [formData, fields]);

  // Input change handler
  const handleChange = (fieldName, value) => {
    const nextFormData = { ...formData, [fieldName]: value };
    setFormData(nextFormData);

    // Validate specific field
    const fieldDef = fields.find((f) => f.name === fieldName);
    if (fieldDef) {
      const err = validateField(fieldDef, value);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: err,
      }));
    }

    if (onFieldChange) {
      const customUpdated = onFieldChange(fieldName, value, nextFormData);
      if (customUpdated && typeof customUpdated === 'object') {
        setFormData(customUpdated);
      }
    }
  };

  // Blur handler to mark field as touched
  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    const fieldDef = fields.find((f) => f.name === fieldName);
    if (fieldDef) {
      const err = validateField(fieldDef, formData[fieldName]);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: err,
      }));
    }
  };

  // Form submit handler
  const handleSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {};
    fields.forEach((f) => {
      allTouched[f.name] = true;
    });
    setTouched(allTouched);

    const { errors: formValidationErrors, isValid } = validateForm(fields, formData);
    setErrors(formValidationErrors);

    if (isValid && onSubmit) {
      onSubmit(formData);
    }
  };

  if (!isOpen) return null;

  // Automatically determine modal size if not explicitly provided
  const modalSize = size || (fields.filter(f => !(f.isPrimaryKey && !isEdit)).length > 4 ? 'large' : 'medium');
  const isLargeGrid = modalSize === 'large';

  const formFieldsContent = fields.map((field) => {
    // Skip primary key or readOnly field when creating if auto-generated
    if (field.isPrimaryKey && !isEdit) {
      return null;
    }

    const fieldError = touched[field.name] || serverErrors[field.name] ? combinedErrors[field.name] : null;
    const isReadOnly = field.readOnly || (isEdit && field.isPrimaryKey);
    const isFullWidth = field.type === 'textarea' || field.gridSpan === 'full';

    return (
      <div
        className="form-group"
        key={field.name}
        style={isLargeGrid && isFullWidth ? { gridColumn: 'span 2' } : {}}
      >
        <label htmlFor={field.name}>
          {field.label} {field.required ? (
            <span className="required-star">*</span>
          ) : (
            <span className="optional-text">(optional)</span>
          )}
        </label>

        {/* Render select dropdown for FK or predefined options */}
        {field.type === 'select' || field.type === 'fk' ? (
          <select
            id={field.name}
            name={field.name}
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            onBlur={() => handleBlur(field.name)}
            disabled={isReadOnly || submitting}
            className={fieldError ? 'input-error' : ''}
          >
            {!field.options?.some(opt => opt.value === '' || opt.value === null || opt.value === undefined) && (
              <option value="">-- Select {field.label} --</option>
            )}
            {field.options &&
              field.options.map((opt) => (
                <option key={String(opt.value)} value={opt.value}>
                  {opt.label || opt.value}
                </option>
              ))}
          </select>
        ) : field.type === 'textarea' ? (
          <textarea
            id={field.name}
            name={field.name}
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            onBlur={() => handleBlur(field.name)}
            disabled={isReadOnly || submitting}
            placeholder={field.placeholder || ''}
            maxLength={field.maxLength}
            rows={field.rows || 3}
            className={fieldError ? 'input-error' : ''}
          />
        ) : field.type === 'checkbox' || field.type === 'boolean' ? (
          <div className="checkbox-field-wrapper">
            <input
              id={field.name}
              name={field.name}
              type="checkbox"
              checked={!!formData[field.name]}
              onChange={(e) => handleChange(field.name, e.target.checked)}
              disabled={isReadOnly || submitting}
              className="checkbox-field-input"
            />
            <span className="checkbox-field-label">
              {field.placeholder || 'Yes'}
            </span>
          </div>
        ) : (
          <input
            id={field.name}
            name={field.name}
            type={
              field.type === 'number' || field.type === 'int' || field.type === 'integer' || field.type === 'decimal'
                ? 'number'
                : field.type === 'date' || field.type === 'datetime'
                  ? 'date'
                  : field.type === 'email'
                    ? 'email'
                    : 'text'
            }
            value={formData[field.name] !== undefined && formData[field.name] !== null ? formData[field.name] : ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            onBlur={() => handleBlur(field.name)}
            disabled={isReadOnly || submitting}
            placeholder={field.placeholder || ''}
            maxLength={field.maxLength}
            min={field.min}
            max={field.max}
            step={field.step}
            className={fieldError ? 'input-error' : ''}
          />
        )}

        {/* Inline field-level error message */}
        {fieldError && (
          <div className="field-error-message">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              style={{
                width: '14px',
                height: '14px',
                flexShrink: 0,
                display: 'inline-block',
                verticalAlign: 'middle',
                marginRight: '4px'
              }}
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            <span>{fieldError}</span>
          </div>
        )}
      </div>
    );
  });

  return (
    <Modal
      title={title}
      onClose={onCancel}
      size={modalSize}
    >
      <form onSubmit={handleSubmit} noValidate>
        {isLargeGrid ? (
          <div className="form-grid">{formFieldsContent}</div>
        ) : (
          formFieldsContent
        )}

        <div className="form-actions-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? (isEdit ? 'Updating...' : 'Saving...') : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

TableForm.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      required: PropTypes.bool,
      maxLength: PropTypes.number,
      placeholder: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.any.isRequired,
        })
      ),
      defaultValue: PropTypes.any,
      readOnly: PropTypes.bool,
      isPrimaryKey: PropTypes.bool,
    })
  ).isRequired,
  initialValues: PropTypes.object,
  mode: PropTypes.oneOf(['create', 'edit']),
  isOpen: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  serverErrors: PropTypes.object,
  onFieldChange: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  itemName: PropTypes.string,
};
